import React, { Component } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";

import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Multiselect from "@cloudscape-design/components/multiselect";
import Toggle from "@cloudscape-design/components/toggle";
import RadioGroup from "@cloudscape-design/components/radio-group";
import Checkbox from "@cloudscape-design/components/checkbox";
import Button from "@cloudscape-design/components/button";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Spinner from "@cloudscape-design/components/spinner";
import SpaceBetween from "@cloudscape-design/components/space-between";
import ColumnLayout from "@cloudscape-design/components/column-layout";

import { partnerSettingsStore } from "../stores/partnerSettings";

const registrationMethodOptions = [
	{ label: "One click", value: "oneClick" },
	{ label: "Phone", value: "phone" },
	{ label: "Email", value: "email" },
	{ label: "Login and password", value: "loginAndPassword" },
	{ label: "VK", value: "vk" },
	{ label: "FB", value: "fb" },
	{ label: "GL", value: "gl" },
	{ label: "OK", value: "ok" },
	{ label: "ST", value: "st" },
	{ label: "TG", value: "tg" },
];

const parseMethods = (value) => {
	if (Array.isArray(value)) {
		return value.map((method) => String(method));
	}

	if (typeof value === "string" && value.length > 0) {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed.map((method) => String(method)) : [];
		} catch (e) {
			return [];
		}
	}

	return [];
};

const getLandingBonusPercent = (data) => {
	if (typeof data?.landingBonusPercent !== "undefined" && data?.landingBonusPercent !== null) {
		return data.landingBonusPercent;
	}

	if (typeof data?.landing_bonus_percent !== "undefined" && data?.landing_bonus_percent !== null) {
		return data.landing_bonus_percent;
	}

	return 740;
};

const getCryptoFtdMinDeposit = (data) => {
	if (typeof data?.cryptoFtdMinDeposit !== "undefined" && data?.cryptoFtdMinDeposit !== null) {
		return data.cryptoFtdMinDeposit;
	}

	if (typeof data?.crypto_ftd_min_deposit !== "undefined" && data?.crypto_ftd_min_deposit !== null) {
		return data.crypto_ftd_min_deposit;
	}

	return 0;
};

const getFtdDisabled = (data) => {
	if (typeof data?.ftdDisabled !== "undefined" && data?.ftdDisabled !== null) {
		return data.ftdDisabled;
	}

	if (typeof data?.ftd_disabled !== "undefined" && data?.ftd_disabled !== null) {
		return data.ftd_disabled;
	}

	return false;
};

const getFtdRulesEnabled = (data) => {
	if (typeof data?.ftdRulesEnabled !== "undefined" && data?.ftdRulesEnabled !== null) {
		return data.ftdRulesEnabled;
	}

	if (typeof data?.ftd_rules_enabled !== "undefined" && data?.ftd_rules_enabled !== null) {
		return data.ftd_rules_enabled;
	}

	return false;
};

const getFtdSkipEveryNth = (data) => {
	if (typeof data?.ftdSkipEveryNth !== "undefined" && data?.ftdSkipEveryNth !== null) {
		return data.ftdSkipEveryNth;
	}

	if (typeof data?.ftd_skip_every_nth !== "undefined" && data?.ftd_skip_every_nth !== null) {
		return data.ftd_skip_every_nth;
	}

	return 0;
};

const getFtdMaxDaysFromRegistration = (data) => {
	if (typeof data?.ftdMaxDaysFromRegistration !== "undefined" && data?.ftdMaxDaysFromRegistration !== null) {
		return data.ftdMaxDaysFromRegistration;
	}

	if (typeof data?.ftd_max_days_from_registration !== "undefined" && data?.ftd_max_days_from_registration !== null) {
		return data.ftd_max_days_from_registration;
	}

	return 0;
};

const formatKopecks = (value) => {
	const amount = Number.parseInt(value, 10) || 0;
	const rubles = Math.floor(amount / 100);
	const kopecks = String(amount % 100).padStart(2, "0");

	return kopecks === "00" ? String(rubles) : `${rubles}.${kopecks}`;
};

const parseRublesToKopecks = (value) => {
	const normalizedAmount = String(value).trim().replace(",", ".");
	const amountMatch = normalizedAmount.match(/^(\d+)(?:\.(\d{1,2}))?$/);

	if (!amountMatch) {
		return null;
	}

	const rubles = Number(amountMatch[1]);
	const kopecks = Number((amountMatch[2] || "").padEnd(2, "0"));
	const amount = rubles * 100 + kopecks;

	return Number.isSafeInteger(amount) ? amount : null;
};

const normalizeForm = (data) => ({
	id: data?.id || 0,
	registrationMethods: parseMethods(data?.registration_methods),
	minDeposit: typeof data?.min_deposit !== "undefined" && data?.min_deposit !== null ? String(data.min_deposit) : "",
	minDepositCount:
		typeof data?.min_deposit_count !== "undefined" && data?.min_deposit_count !== null ? String(data.min_deposit_count) : "",
	redirectPath: typeof data?.redirect_path !== "undefined" && data?.redirect_path !== null ? String(data.redirect_path) : "",
	autoFillPromocode: normalizeBoolean(typeof data?.autoFillPromocode !== "undefined" ? data.autoFillPromocode : data?.auto_fill_promocode),
	openDepositAfterRegistration: normalizeBoolean(
		typeof data?.openDepositAfterRegistration !== "undefined" ? data.openDepositAfterRegistration : data?.open_deposit_after_registration,
	),
	landingRedirectEnabled: normalizeBoolean(
		typeof data?.landingRedirectEnabled !== "undefined" ? data.landingRedirectEnabled : data?.landing_redirect_enabled,
	),
	landingBonusPercent: String(getLandingBonusPercent(data)),
	ftdDisabled: normalizeBoolean(getFtdDisabled(data)),
	ftdRulesEnabled: normalizeBoolean(getFtdRulesEnabled(data)),
	ftdSkipEveryNth: String(getFtdSkipEveryNth(data)),
	ftdMaxDaysFromRegistration: String(getFtdMaxDaysFromRegistration(data)),
	cryptoFtdMinDeposit: formatKopecks(getCryptoFtdMinDeposit(data)),
});

const getErrorText = (error) => {
	if (!error) {
		return "";
	}

	if (typeof error === "string") {
		return error;
	}

	if (error.text) {
		return error.text;
	}

	if (error.message) {
		return error.message;
	}

	return "";
};

const normalizeBoolean = (value) => {
	if (typeof value === "boolean") {
		return value;
	}

	if (typeof value === "number") {
		return value === 1;
	}

	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();

		if (["1", "true", "enabled", "yes"].includes(normalized)) {
			return true;
		}

		if (["0", "false", "disabled", "no", "", "null", "undefined"].includes(normalized)) {
			return false;
		}
	}

	return Boolean(value);
};

const getPostbackUidChecked = (response) => {
	if (response && typeof response === "object") {
		if (Object.prototype.hasOwnProperty.call(response, "enabled")) {
			return normalizeBoolean(response.enabled);
		}

		if (Object.prototype.hasOwnProperty.call(response, "active")) {
			return normalizeBoolean(response.active);
		}

		if (Object.prototype.hasOwnProperty.call(response, "checked")) {
			return normalizeBoolean(response.checked);
		}

		if (Object.prototype.hasOwnProperty.call(response, "value")) {
			return normalizeBoolean(response.value);
		}

		if (Object.prototype.hasOwnProperty.call(response, "result")) {
			return normalizeBoolean(response.result);
		}
	}

	return normalizeBoolean(response);
};

const isValidLandingBonusPercent = (value) => /^\d+$/.test(String(value).trim());
const hasOptionalValue = (value) => String(value).trim() !== "";

const getCryptoFtdMinDepositError = (value) =>
	parseRublesToKopecks(value) === null ? "Enter a non-negative RUB amount with up to two decimal places." : "";

const getNonNegativeIntegerError = (value) =>
	/^\d+$/.test(String(value).trim()) && Number.isSafeInteger(Number(value)) ? "" : "Enter a non-negative integer.";

const getDepositError = (form) => {
	const hasMinDeposit = hasOptionalValue(form.minDeposit);
	const hasMinDepositCount = hasOptionalValue(form.minDepositCount);

	if (hasMinDeposit !== hasMinDepositCount) {
		return "Set both Min deposit and Deposit count, or leave both empty.";
	}

	if (!hasMinDeposit) {
		return "";
	}

	if (!Number.isFinite(Number(form.minDeposit)) || Number(form.minDeposit) < 0) {
		return "Min deposit must be a non-negative number.";
	}

	if (!/^\d+$/.test(String(form.minDepositCount).trim())) {
		return "Deposit count must be a non-negative integer.";
	}

	return "";
};

const formsEqual = (left, right) => {
	if (!left || !right) {
		return false;
	}

	const leftMethods = [...left.registrationMethods].sort();
	const rightMethods = [...right.registrationMethods].sort();

	if (leftMethods.length !== rightMethods.length) {
		return false;
	}

	for (let i = 0; i < leftMethods.length; i += 1) {
		if (leftMethods[i] !== rightMethods[i]) {
			return false;
		}
	}

	return (
		Number(left.id) === Number(right.id) &&
		left.minDeposit === right.minDeposit &&
		left.minDepositCount === right.minDepositCount &&
		left.redirectPath === right.redirectPath &&
		left.autoFillPromocode === right.autoFillPromocode &&
		left.openDepositAfterRegistration === right.openDepositAfterRegistration &&
		left.landingRedirectEnabled === right.landingRedirectEnabled &&
		left.landingBonusPercent === right.landingBonusPercent &&
		left.ftdDisabled === right.ftdDisabled &&
		left.ftdRulesEnabled === right.ftdRulesEnabled &&
		left.ftdSkipEveryNth === right.ftdSkipEveryNth &&
		left.ftdMaxDaysFromRegistration === right.ftdMaxDaysFromRegistration &&
		left.cryptoFtdMinDeposit === right.cryptoFtdMinDeposit
	);
};

class PlayerProfilePartnerSettingsSection extends Component {
	constructor(props) {
		super(props);
		this.unmounted = false;
		this.postbackRequestId = 0;

		this.state = {
			form: normalizeForm(false),
			touched: false,
			postbackUidChecked: false,
			postbackUidLoaded: false,
			postbackUidLoading: false,
			postbackUidError: null,
		};
	}

	handleFieldChange = (patch) => {
		this.setState((prevState) => ({
			touched: true,
			form: {
				...prevState.form,
				...patch,
			},
		}));
	};

	handleSave = () => {
		const { form } = this.state;
		const cryptoFtdMinDeposit = parseRublesToKopecks(form.cryptoFtdMinDeposit);

		if (
			!isValidLandingBonusPercent(form.landingBonusPercent) ||
			getDepositError(form) ||
			cryptoFtdMinDeposit === null ||
			getNonNegativeIntegerError(form.ftdSkipEveryNth) ||
			getNonNegativeIntegerError(form.ftdMaxDaysFromRegistration)
		) {
			return;
		}

		const minDepositNumber = hasOptionalValue(form.minDeposit) ? Number(form.minDeposit) : null;
		const minDepositCountNumber = hasOptionalValue(form.minDepositCount) ? Number(form.minDepositCount) : null;

		const payload = {
			refererUid: this.props.uid,
			registrationMethods: form.registrationMethods,
			minDeposit: minDepositNumber,
			minDepositCount: minDepositCountNumber,
			redirectPath: form.redirectPath.trim().replace(/^\/+/, "") || null,
			autoFillPromocode: form.autoFillPromocode,
			openDepositAfterRegistration: form.openDepositAfterRegistration,
			landingRedirectEnabled: form.landingRedirectEnabled,
			landingBonusPercent: Number(form.landingBonusPercent),
			ftdDisabled: form.ftdDisabled,
			ftdRulesEnabled: form.ftdRulesEnabled,
			ftdSkipEveryNth: Number(form.ftdSkipEveryNth),
			ftdMaxDaysFromRegistration: Number(form.ftdMaxDaysFromRegistration),
			cryptoFtdMinDeposit,
		};

		this.setState({ touched: false });

		if (Number(form.id) > 0) {
			this.props.Interface.loadUserPartnerSettingsEdit({
				id: form.id,
				...payload,
			});
			return;
		}

		this.props.Interface.loadUserPartnerSettingsAdd(payload);
	};

	loadPostbackUidState = (uid = this.props.uid) => {
		const requestUid = uid;
		const requestId = ++this.postbackRequestId;

		this.setState({
			postbackUidLoading: true,
			postbackUidLoaded: false,
			postbackUidError: null,
		});

		return this.props.Interface.checkPartnerSettingsPostbackUid({ id: requestUid })
			.then((response) => {
				if (this.unmounted || requestId !== this.postbackRequestId || requestUid !== this.props.uid) {
					return response;
				}

				this.setState({
					postbackUidChecked: getPostbackUidChecked(response),
					postbackUidLoaded: true,
					postbackUidLoading: false,
					postbackUidError: null,
				});

				return response;
			})
			.catch((error) => {
				if (this.unmounted || requestId !== this.postbackRequestId || requestUid !== this.props.uid) {
					return error;
				}

				this.setState({
					postbackUidChecked: false,
					postbackUidLoaded: false,
					postbackUidLoading: false,
					postbackUidError: error,
				});

				return error;
			});
	};

	handlePostbackUidChange = ({ detail }) => {
		const requestUid = this.props.uid;
		const requestId = ++this.postbackRequestId;
		const nextChecked = detail.checked;
		const previousChecked = this.state.postbackUidChecked;

		this.setState({
			postbackUidLoading: true,
			postbackUidError: null,
		});

		this.props.Interface.updatePartnerSettingsPostbackUid({ id: requestUid, enabled: nextChecked })
			.then(() => {
				if (this.unmounted || requestId !== this.postbackRequestId || requestUid !== this.props.uid) {
					return;
				}

				this.setState({
					postbackUidChecked: nextChecked,
					postbackUidLoaded: true,
					postbackUidLoading: false,
					postbackUidError: null,
				});
			})
			.catch((error) => {
				if (this.unmounted || requestId !== this.postbackRequestId || requestUid !== this.props.uid) {
					return;
				}

				this.setState({
					postbackUidChecked: previousChecked,
					postbackUidLoaded: true,
					postbackUidLoading: false,
					postbackUidError: error,
				});
			});
	};

	componentDidMount() {
		this.props.Interface.loadUserPartnerSettingsList(this.props.uid);
		this.loadPostbackUidState(this.props.uid);
	}

	componentDidUpdate(prevProps) {
		const prevUid = prevProps.uid;
		const currentUid = this.props.uid;

		if (prevUid !== currentUid) {
			this.setState(
				{
					touched: false,
					form: normalizeForm(false),
					postbackUidChecked: false,
					postbackUidLoaded: false,
					postbackUidLoading: false,
					postbackUidError: null,
				},
				() => {
					this.props.Interface.loadUserPartnerSettingsList(currentUid);
					this.loadPostbackUidState(currentUid);
				},
			);
			return;
		}

		if (prevProps.partnerSettings.data !== this.props.partnerSettings.data && !this.state.touched) {
			this.setState({
				form: normalizeForm(this.props.partnerSettings.data),
			});
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.postbackRequestId += 1;
		this.props.resetPartnerSettings();
	}

	render() {
		const { partnerSettings } = this.props;
		const { form, postbackUidChecked, postbackUidLoaded, postbackUidLoading, postbackUidError } = this.state;
		const methodsSelected = registrationMethodOptions.filter((option) => form.registrationMethods.includes(option.value));
		const sourceForm = normalizeForm(partnerSettings.data);
		const isFormUnchanged = formsEqual(form, sourceForm);
		const landingBonusPercentError = isValidLandingBonusPercent(form.landingBonusPercent)
			? ""
			: "Enter a non-negative integer.";
		const depositError = getDepositError(form);
		const cryptoFtdMinDepositError = getCryptoFtdMinDepositError(form.cryptoFtdMinDeposit);
		const ftdSkipEveryNthError = getNonNegativeIntegerError(form.ftdSkipEveryNth);
		const ftdMaxDaysFromRegistrationError = getNonNegativeIntegerError(form.ftdMaxDaysFromRegistration);

		const isSaveDisabled =
			!partnerSettings.loaded ||
			isFormUnchanged ||
			Boolean(landingBonusPercentError) ||
			Boolean(depositError) ||
			Boolean(cryptoFtdMinDepositError) ||
			Boolean(ftdSkipEveryNthError) ||
			Boolean(ftdMaxDaysFromRegistrationError);
		const errorText = getErrorText(partnerSettings.error);
		const postbackUidErrorText = getErrorText(postbackUidError);

		return (
			<Container
				header={
					<Header
						variant="h3"
						description="Optional registration, deposit, FTD, landing and postback rules for this partner"
						actions={
							<Button variant="primary" onClick={this.handleSave} loading={!partnerSettings.loaded} disabled={isSaveDisabled}>
								Save
							</Button>
						}>
						Partner Settings
					</Header>
				}>
				<SpaceBetween size="l" direction="vertical">
					{Boolean(errorText) && (
						<Alert type="error" header="Failed to load or save partner settings">
							{errorText}
						</Alert>
					)}

					{Boolean(postbackUidErrorText) && (
						<Alert type="error" header="Failed to load or update postback UID">
							{postbackUidErrorText}
						</Alert>
					)}

					<Container
						header={
							<Header variant="h4" description="Sign-up methods and what happens after registration">
								Registration
							</Header>
						}>
						<SpaceBetween size="m" direction="vertical">
							<FormField label="Registration methods">
								<Multiselect
									selectedOptions={methodsSelected}
									onChange={({ detail }) => {
										this.handleFieldChange({
											registrationMethods: detail.selectedOptions.map((option) => option.value),
										});
									}}
									options={registrationMethodOptions}
									placeholder="Choose registration methods"
									disabled={!partnerSettings.loaded}
								/>
							</FormField>

							<ColumnLayout columns={2}>
								<FormField label="Autofill promocode">
									<Toggle
										checked={form.autoFillPromocode}
										disabled={!partnerSettings.loaded}
										onChange={({ detail }) => {
											this.handleFieldChange({
												autoFillPromocode: detail.checked,
											});
										}}>
										Enabled
									</Toggle>
								</FormField>

								<FormField label="Open deposit after registration">
									<Toggle
										checked={form.openDepositAfterRegistration}
										disabled={!partnerSettings.loaded}
										onChange={({ detail }) => {
											this.handleFieldChange({
												openDepositAfterRegistration: detail.checked,
											});
										}}>
										Enabled
									</Toggle>
								</FormField>
							</ColumnLayout>
						</SpaceBetween>
					</Container>

					<Container
						header={
							<Header variant="h4" description="Minimum deposit amount and how many deposits it applies to">
								Deposits
							</Header>
						}>
						<SpaceBetween size="m" direction="vertical">
							<FormField label="Min deposit" errorText={depositError}>
								<Input
									value={form.minDeposit}
									type="number"
									inputMode="decimal"
									disabled={!partnerSettings.loaded}
									onChange={({ detail }) => {
										this.handleFieldChange({
											minDeposit: detail.value,
										});
									}}
								/>
							</FormField>

							<FormField
								label="Deposit count"
								description="0 applies the minimum to all deposits; N applies it to the first N deposits."
								errorText={depositError}>
								<Input
									value={form.minDepositCount}
									type="number"
									inputMode="numeric"
									disabled={!partnerSettings.loaded}
									onChange={({ detail }) => {
										this.handleFieldChange({
											minDepositCount: detail.value,
										});
									}}
								/>
							</FormField>
						</SpaceBetween>
					</Container>

					<Container
						header={
							<Header variant="h4" description="First-time deposit tracking, limits and exclusions">
								FTD
							</Header>
						}>
						<SpaceBetween size="m" direction="vertical">
							<FormField
								label="Enable FTD rules"
								description="When disabled, Project-Core uses the legacy FTD behavior and ignores all rules below.">
								<Toggle
									checked={form.ftdRulesEnabled}
									disabled={!partnerSettings.loaded}
									onChange={({ detail }) => {
										this.handleFieldChange({
											ftdRulesEnabled: detail.checked,
										});
									}}>
									{form.ftdRulesEnabled ? "Enabled" : "Disabled"}
								</Toggle>
							</FormField>

							<FormField
								label="Ignore FTD"
								description="When enabled, FTD is not counted for this partner and other FTD rules are ignored.">
								<RadioGroup
									value={form.ftdDisabled ? "enabled" : "disabled"}
									disabled={!partnerSettings.loaded || !form.ftdRulesEnabled}
									items={[
										{ value: "enabled", label: "Enabled" },
										{ value: "disabled", label: "Disabled" },
									]}
									onChange={({ detail }) => {
										this.handleFieldChange({
											ftdDisabled: detail.value === "enabled",
										});
									}}
								/>
							</FormField>

							<FormField
								label="Minimum crypto deposit"
								description="RUB. 0 disables the limit."
								errorText={cryptoFtdMinDepositError}>
								<Input
									value={form.cryptoFtdMinDeposit}
									type="number"
									inputMode="decimal"
									min="0"
									step="0.01"
									disabled={!partnerSettings.loaded || !form.ftdRulesEnabled || form.ftdDisabled}
									onChange={({ detail }) => {
										this.handleFieldChange({
											cryptoFtdMinDeposit: detail.value,
										});
									}}
								/>
							</FormField>

							<FormField
								label="Skip every Nth FTD"
								description="0 disables cuts, but eligible FTD numbering continues. FTDs rejected by other rules do not count."
								errorText={ftdSkipEveryNthError}>
								<Input
									value={form.ftdSkipEveryNth}
									type="number"
									inputMode="numeric"
									min="0"
									step="1"
									disabled={!partnerSettings.loaded || !form.ftdRulesEnabled || form.ftdDisabled}
									onChange={({ detail }) => {
										this.handleFieldChange({
											ftdSkipEveryNth: detail.value,
										});
									}}
								/>
							</FormField>

							<FormField
								label="Max days from registration"
								description="0 disables the rule. Later first deposits are rejected."
								errorText={ftdMaxDaysFromRegistrationError}>
								<Input
									value={form.ftdMaxDaysFromRegistration}
									type="number"
									inputMode="numeric"
									min="0"
									step="1"
									disabled={!partnerSettings.loaded || !form.ftdRulesEnabled || form.ftdDisabled}
									onChange={({ detail }) => {
										this.handleFieldChange({
											ftdMaxDaysFromRegistration: detail.value,
										});
									}}
								/>
							</FormField>
						</SpaceBetween>
					</Container>

					<Container
						header={
							<Header variant="h4" description="Where partner traffic is sent and what the landing shows">
								Redirect and landing
							</Header>
						}>
						<SpaceBetween size="m" direction="vertical">
							<FormField label="Redirect partner traffic to landing">
								<Toggle
									checked={form.landingRedirectEnabled}
									disabled={!partnerSettings.loaded}
									onChange={({ detail }) => {
										this.handleFieldChange({
											landingRedirectEnabled: detail.checked,
										});
									}}>
									Enabled
								</Toggle>
							</FormField>

							<FormField
								label="Landing bonus percent"
								description="Bonus shown on the partner registration landing."
								errorText={landingBonusPercentError}>
								<Input
									value={form.landingBonusPercent}
									type="number"
									inputMode="numeric"
									min="0"
									step="1"
									disabled={!partnerSettings.loaded}
									onChange={({ detail }) => {
										this.handleFieldChange({
											landingBonusPercent: detail.value,
										});
									}}
								/>
							</FormField>

							<FormField label="Internal redirect path" description="Example: lobby/0/1 or lobby. Do not add the first slash.">
								<Input
									value={form.redirectPath}
									placeholder="lobby/0/1"
									disabled={!partnerSettings.loaded}
									onChange={({ detail }) => {
										this.handleFieldChange({
											redirectPath: detail.value,
										});
									}}
								/>
							</FormField>
						</SpaceBetween>
					</Container>

					<Container
						header={
							<Header variant="h4" description="Applies immediately, without Save">
								Postback
							</Header>
						}>
						<FormField label="Postback UID">
							{postbackUidLoading ? (
								<Box padding={{ top: "xs", bottom: "xs" }}>
									<Spinner size="normal" />
								</Box>
							) : (
								<Checkbox checked={postbackUidChecked} disabled={!postbackUidLoaded} onChange={this.handlePostbackUidChange}>
									Enabled
								</Checkbox>
							)}
						</FormField>
					</Container>

					<Box float="right">
						<Button variant="primary" onClick={this.handleSave} loading={!partnerSettings.loaded} disabled={isSaveDisabled}>
							Save
						</Button>
					</Box>
				</SpaceBetween>
			</Container>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const partnerSettings = useRecoilValue(partnerSettingsStore);
		const resetPartnerSettings = useResetRecoilState(partnerSettingsStore);

		return <Component {...props} partnerSettings={partnerSettings} resetPartnerSettings={resetPartnerSettings} />;
	};
}

export default withRecoil(PlayerProfilePartnerSettingsSection);
